import type {
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewRow,
} from "../../../../../../../../../../api/kehrblatt/planning"
import type {
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
} from "../../../../../../../../../../api/kehrblatt/schedule-events"
import { buildCalendarRouteTransitionsFromEvents } from "../../../../../../../_logic/route-transitions"
import type { EventUpdatePayload } from "../../../../../../../_logic/calendar-types"
import type {
  AppointmentPlanningCalendarAppointmentChange,
  AppointmentPlanningCalendarContext,
  AppointmentPlanningCalendarPreviewModel,
  AppointmentPlanningCalendarSource,
} from "../../../../../_shared/_logic/calendar-contract"
import { buildAppointmentPlanningCalendarPreviewModel } from "./calendarPreviewModel"
import { buildPlanningRouteAnchorUpdatesFromTransitions } from "./routeAnchorUpdates"
import {
  appointmentMeetsStartBound,
  type AppointmentPlanningStartBound,
} from "../../../../../_shared/_logic/start-bound"

const PREVIEW_EVENT_PREFIX = "preview-event:"

export const clientRowIdFromPreviewEventId = (eventId?: string | null): string | null => {
  const value = String(eventId ?? "")

  return value.startsWith(PREVIEW_EVENT_PREFIX) ? value.slice(PREVIEW_EVENT_PREFIX.length) : null
}

export const durationMinutesFromRange = (start: Date, end: Date): number | null => {
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000)

  return Number.isFinite(minutes) && minutes > 0 ? minutes : null
}

const dedupeById = <T extends { id?: string | null }>(items: T[]): T[] => {
  const result = new Map<string, T>()
  for (const item of items) {
    const id = String(item?.id ?? "").trim()

    if (id && !result.has(id)) result.set(id, item)
  }

  return Array.from(result.values())
}

export const buildFilteredCombinedAppointmentPlanningEvents = ({
  boardEvents,
  model,
  context,
}: {
  boardEvents: ScheduleEvent[]
  model: AppointmentPlanningCalendarPreviewModel
  context?: AppointmentPlanningCalendarContext | null
}): ScheduleEvent[] => {
  const hiddenBoardEventIds = new Set(model.hiddenBoardEventIds)
  const employeeIds = context?.employeeIds ?? model.employeeIds
  const rangeStart = context?.rangeStart ?? model.rangeStart
  const rangeEnd = context?.rangeEnd ?? model.rangeEnd
  const visibleBoardEvents = boardEvents.filter(
    (event) => !hiddenBoardEventIds.has(String(event.id ?? "")),
  )

  return dedupeById([...visibleBoardEvents, ...model.previewEvents]).filter((event) => {
    if (event.status === "CANCELED" || event.status === "NOSHOW") return false

    if (!employeeIds.includes(String(event.employee_id ?? ""))) return false

    const start = new Date(event.start_at)
    const end = new Date(event.end_at)

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false

    return rangeStart && rangeEnd ? start < rangeEnd && end > rangeStart : true
  })
}

const rowIsInCalendarContext = (
  row: AppointmentPlanningPreviewRow,
  context: AppointmentPlanningCalendarContext,
): boolean => {
  const appointment = row.appointment

  if (row.status !== "planned" || !appointment?.start_at) return false

  if (!context.employeeIds.includes(String(appointment.employee_id ?? ""))) return false

  const start = new Date(appointment.start_at)

  return Number.isFinite(start.getTime()) && start >= context.rangeStart && start < context.rangeEnd
}

const buildRouteAnchorUpdates = ({
  organizationId,
  rows,
  sources,
  employeeLabelById,
  now,
  boardEvents,
  boardWorkOrders,
  boardProperties,
  context,
}: {
  organizationId: string
  rows: AppointmentPlanningPreviewRow[]
  sources: AppointmentPlanningCalendarSource[]
  employeeLabelById: Record<string, string>
  now: Date
  boardEvents: ScheduleEvent[]
  boardWorkOrders: CalendarBoardWorkOrderRef[]
  boardProperties: CalendarBoardPropertyRef[]
  context?: AppointmentPlanningCalendarContext | null
}) => {
  const nextPreviewModel = buildAppointmentPlanningCalendarPreviewModel({
    organizationId,
    rows,
    sources,
    employeeLabelById,
    now,
  })
  const nextCombinedEvents = buildFilteredCombinedAppointmentPlanningEvents({
    boardEvents,
    model: nextPreviewModel,
    context,
  })
  const nextCombinedWorkOrders = dedupeById([
    ...boardWorkOrders,
    ...nextPreviewModel.previewWorkOrders,
  ])
  const nextCombinedProperties = dedupeById([
    ...boardProperties,
    ...nextPreviewModel.previewProperties,
  ])
  const nextWorkOrderById = Object.fromEntries(
    nextCombinedWorkOrders.map((order) => [order.id, order]),
  )
  const nextPropertyById = Object.fromEntries(
    nextCombinedProperties.map((property) => [property.id, property]),
  )
  const nextTransitions = buildCalendarRouteTransitionsFromEvents({
    events: nextCombinedEvents,
    workOrderById: nextWorkOrderById,
    propertyById: nextPropertyById,
  })

  return buildPlanningRouteAnchorUpdatesFromTransitions({
    rows: context ? rows.filter((row) => rowIsInCalendarContext(row, context)) : rows,
    events: nextCombinedEvents,
    workOrderById: nextWorkOrderById,
    propertyById: nextPropertyById,
    transitions: nextTransitions,
  })
}

export const appointmentFromMovePayload = ({
  payload,
  previousAppointment,
  employeeLabelById,
}: {
  payload: EventUpdatePayload
  previousAppointment?: AppointmentPlanningPreviewAppointment | null
  employeeLabelById: Record<string, string>
}): AppointmentPlanningPreviewAppointment | null => {
  const employeeId = String(payload.employeeId ?? payload.event.employee_id ?? "").trim()

  if (!employeeId) return null

  const flexType: "HARD" | "SOFT" = payload.flexType === "SOFT" ? "SOFT" : "HARD"
  const timeframeStart = payload.timeframeStart ?? payload.start
  const timeframeEnd = payload.timeframeEnd ?? payload.end

  return {
    start_at: payload.start.toISOString(),
    end_at: payload.end.toISOString(),
    employee_id: employeeId,
    employee_name: employeeLabelById[employeeId] ?? payload.event.employee_name ?? null,
    flex_type: flexType,
    timeframe_start_at: (timeframeStart ?? payload.start).toISOString(),
    timeframe_end_at: (timeframeEnd ?? payload.end).toISOString(),
    customer_window_start_at: null,
    customer_window_end_at: null,
    customer_availability_match: previousAppointment?.customer_availability_match ?? null,
    previous_anchor: previousAppointment?.previous_anchor ?? null,
    next_anchor: previousAppointment?.next_anchor ?? null,
  }
}

export const buildCalendarAppointmentChangeFromBoardUpdate = ({
  startBound,
  organizationId,
  payload,
  rows,
  sources,
  employeeLabelById,
  now,
  boardEvents,
  boardWorkOrders,
  boardProperties,
  context,
}: {
  startBound: AppointmentPlanningStartBound
  organizationId: string
  payload: EventUpdatePayload
  rows: AppointmentPlanningPreviewRow[]
  sources: AppointmentPlanningCalendarSource[]
  employeeLabelById: Record<string, string>
  now: Date
  boardEvents: ScheduleEvent[]
  boardWorkOrders: CalendarBoardWorkOrderRef[]
  boardProperties: CalendarBoardPropertyRef[]
  context?: AppointmentPlanningCalendarContext | null
}): AppointmentPlanningCalendarAppointmentChange | null => {
  const clientRowId = clientRowIdFromPreviewEventId(payload.event.id)

  if (!clientRowId) return null

  const currentRow = rows.find((row) => row.client_row_id === clientRowId)

  if (!currentRow) return null

  const appointment = appointmentFromMovePayload({
    payload,
    previousAppointment: currentRow.appointment ?? null,
    employeeLabelById,
  })

  if (!appointment || !appointmentMeetsStartBound(appointment, startBound)) return null

  const nextRows: AppointmentPlanningPreviewRow[] = rows.map((row) =>
    row.client_row_id === clientRowId
      ? {
          ...row,
          status: "planned",
          appointment,
          effective_planning_strategy: "MANUAL",
        }
      : row,
  )

  return {
    clientRowId,
    appointment,
    routeAnchorUpdatesByRowId: buildRouteAnchorUpdates({
      organizationId,
      rows: nextRows,
      sources,
      employeeLabelById,
      now,
      boardEvents,
      boardWorkOrders,
      boardProperties,
      context,
    }),
    durationMinutes: durationMinutesFromRange(payload.start, payload.end),
  }
}
